import React, { useState } from 'react';
import { 
  Users, Gift, Link2, Copy, TrendingUp, CircleDollarSign, 
  ArrowUpRight, Share2, Info, Check, ShieldCheck 
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { 
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle 
} from "@/components/ui/sheet";
import { useAuth } from "@/integrations/supabase/AuthProvider";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const AffiliatePanel = ({ open, onOpenChange }) => {
  const { profile } = useAuth();
  const [copied, setCopied] = useState(false);

  const refCode = profile?.referral_code || "CODE_ERR";
  const refLink = `${window.location.origin}/login?ref=${refCode}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(refLink);
    setCopied(true);
    toast.success("Affiliate link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="glass-surface h-full border-white/5 bg-sonas-bg p-0 text-sonas-text w-full sm:max-w-md overflow-hidden flex flex-col" side="right">
        <SheetHeader className="p-6 border-b border-white/5 bg-sonas-bg/50 backdrop-blur-xl">
          <div className="flex items-center justify-between">
            <div>
              <SheetTitle className="text-2xl font-syne flex items-center gap-2">
                Sonas Affiliate <span className="text-[10px] font-bold uppercase bg-blue-500/10 text-blue-400 px-2 py-0.5 rounded-full">Partner</span>
              </SheetTitle>
              <SheetDescription className="text-sonas-muted">Expand the grid and earn architectural credits.</SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-6 space-y-8 sonas-scrollbar">
          {/* Link Section */}
          <section className="space-y-4">
            <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-sonas-dim">
              <Link2 size={14} className="text-sonas-accent" /> Your Referral Bridge
            </div>
            <div className="bg-white/[0.03] border border-white/5 p-4 rounded-2xl space-y-4">
              <p className="text-xs text-sonas-muted leading-relaxed">
                Invite fellow creators to Sonas. When they join using your link, you'll earn rewards on their first architectural upgrades.
              </p>
              <div className="flex gap-2">
                <div className="flex-1 bg-black/40 border border-white/5 rounded-xl h-11 px-4 flex items-center overflow-hidden">
                  <span className="text-xs font-mono text-sonas-accent truncate">{refLink}</span>
                </div>
                <Button 
                  onClick={handleCopy}
                  className={cn("h-11 w-11 rounded-xl transition-all", copied ? "bg-green-500 text-white" : "bg-sonas-accent text-sonas-bg")}
                  size="icon"
                >
                  {copied ? <Check size={18} /> : <Copy size={18} />}
                </Button>
              </div>
            </div>
          </section>

          {/* Rewards Grid */}
          <section className="space-y-4">
             <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-sonas-dim">
              <Gift size={14} className="text-blue-400" /> Reward Tiers
            </div>
            <div className="grid grid-cols-1 gap-3">
              <div className="bg-white/[0.02] border border-white/5 p-5 rounded-2xl flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-bold">Pro Conversion</p>
                  <p className="text-[10px] text-sonas-dim">Referral upgrades to Architect Pro</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-syne font-bold text-sonas-accent">+$5.00</p>
                  <p className="text-[8px] font-bold uppercase text-sonas-muted">Instant Credit</p>
                </div>
              </div>
              <div className="bg-white/[0.02] border border-white/5 p-5 rounded-2xl flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-bold">Pack Commission</p>
                  <p className="text-[10px] text-sonas-dim">Referral buys any credit pack</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-syne font-bold text-blue-400">20%</p>
                  <p className="text-[8px] font-bold uppercase text-sonas-muted">Value Bonus</p>
                </div>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="bg-sonas-accent/5 border border-sonas-accent/20 rounded-3xl p-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-10"><TrendingUp size={60} /></div>
            <div className="relative z-10 space-y-6">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-sonas-accent">Affiliate Performance</p>
              <div className="grid grid-cols-2 gap-8">
                <div className="space-y-1">
                  <p className="text-3xl font-syne font-bold">0</p>
                  <p className="text-[9px] font-bold uppercase text-sonas-muted tracking-widest">Active Referrals</p>
                </div>
                <div className="space-y-1">
                  <p className="text-3xl font-syne font-bold text-sonas-accent">$0.00</p>
                  <p className="text-[9px] font-bold uppercase text-sonas-muted tracking-widest">Lifetime Earned</p>
                </div>
              </div>
            </div>
          </section>
        </div>

        <div className="p-6 bg-sonas-bg border-t border-white/5">
          <section className="bg-blue-500/5 border border-blue-500/20 p-4 rounded-2xl flex items-start gap-4">
            <ShieldCheck className="h-5 w-5 text-blue-400 shrink-0 mt-0.5" />
            <p className="text-[10px] leading-relaxed text-sonas-muted">
              <span className="font-bold text-blue-400">AUDIT NOTE:</span> Referral rewards are applied automatically upon payment verification. Credits earned through affiliates cannot be withdrawn as cash.
            </p>
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default AffiliatePanel;